import React, { useEffect, useState } from "react";
import { Star, ThumbsUp, GraduationCap } from "lucide-react";
import api, { formatMoney, formatNumber } from "../lib/api";
import { useConsejoExcluidos } from "../lib/consejoExcluidos";
import { ExcluirConsejoBtn } from "./ExcluirConsejoBtn";

const per100 = (v) => v.toLocaleString("es-ES", { maximumFractionDigits: 1 });

function Persona({ f, name, tone }) {
  return (
    <li className="flex justify-between gap-2 text-xs">
      <span className="truncate">{name || f.vendedor}</span>
      <span className={`font-mono shrink-0 ${tone}`}>{f.veces} · {per100(f.por_100_recetas)}/100</span>
    </li>
  );
}

function Producto({ p, i, names, excl }) {
  return (
    <li className="border border-slate-200 rounded-lg p-4 transition-shadow hover:shadow-md" data-testid={`producto-estrella-${i}`}>
      <div className="flex items-start gap-3">
        <span className="h-7 w-7 shrink-0 rounded-full bg-amber-50 text-amber-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-semibold">{p.descripcion}</div>
            <ExcluirConsejoBtn cn={p.cn} descripcion={p.descripcion} excl={excl} testid={`producto-estrella-${i}-excluir`} />
          </div>
          <div className="text-[11px] text-slate-500 font-mono">
            CN {p.cn} · {formatNumber(p.operaciones)} recetas · {formatNumber(p.uds)} uds · {formatMoney(p.facturacion)} · lo aconsejan {p.empleados_que_lo_aconsejan}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 pt-3 border-t border-slate-100">
        <div data-testid={`producto-estrella-${i}-mas`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1 mb-1"><ThumbsUp size={11} /> Lo aconsejan más</div>
          <ul className="space-y-0.5">{p.mas.map((f) => <Persona key={f.vendedor} f={f} name={names[f.vendedor]} tone="text-emerald-700" />)}</ul>
        </div>
        <div data-testid={`producto-estrella-${i}-menos`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-red-600 flex items-center gap-1 mb-1"><GraduationCap size={11} /> Casi nunca · a formar</div>
          <ul className="space-y-0.5">{p.menos.map((f) => <Persona key={f.vendedor} f={f} name={names[f.vendedor]} tone="text-red-600" />)}</ul>
        </div>
      </div>
    </li>
  );
}

export const ProductosEstrella = ({ importId }) => {
  const [data, setData] = useState(null);
  const [names, setNames] = useState({});
  const excl = useConsejoExcluidos();
  useEffect(() => {
    api.get(`/sales/imports/${importId}/productos-estrella`).then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/staff").then((r) => setNames(Object.fromEntries((r.data.personal || []).map((s) => [s.vendedor, s.nombre])))).catch(() => {});
  }, [importId]);
  if (!data?.productos?.length) return null;
  return (
    <div className="card p-6 mb-6" data-testid="productos-estrella-panel">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Star size={12} /> Formación</div>
        <h3 className="font-display text-xl font-bold">Productos estrella del consejo</h3>
        <p className="text-xs text-slate-500 mt-1">
          Los 10 productos de parafarmacia que más se añaden a una receta. En cada uno, quién lo aconseja más y quién casi nunca
          (veces · por cada 100 recetas atendidas; solo empleados con 30 recetas o más).
        </p>
      </div>
      <ol className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {data.productos.filter((p) => !excl.set.has(p.cn)).map((p, i) => <Producto key={p.cn} p={p} i={i} names={names} excl={excl} />)}
      </ol>
    </div>
  );
};
